import { cva, type VariantProps } from "class-variance-authority";
import type { HTMLAttributes, ReactNode } from "react";
import { cn } from "../cn";
import { Button } from "./button";

/**
 * A list as one surface, rather than a stack of them.
 *
 * This is the house convention broken most often, and both shells carried a
 * private copy of it before this file existed. A card per row draws a border
 * and a shadow around every item, so a shelf of two games reads as a wall of
 * mostly-empty boxes and forty rows read as forty things competing with each
 * other. One surface, rows divided by a hairline, is a list.
 *
 * **It is a list, and says so.** Most of these replaced a `<table>`, and a
 * `<tr>` carries `role="row"` for free where a `<div>` carries nothing — so the
 * first screens to migrate silently dropped out of the accessible tree, taking
 * `getByRole("row")` with them. `list`/`listitem` rather than `table`/`row`
 * because that is what these are: the house rule is *one surface with
 * rule-divided rows*, and the figures are labelled individually precisely so
 * there are no columns to announce. Pass `role` to override.
 *
 * Known gap: a `RowList` of `RowGroup`s nests the rows one level deeper than
 * this markup claims. Grouped lists want the group to be the list and the
 * outer container to step out of the way; until that is done, pass
 * `role="presentation"` to the outer `RowList` when you group.
 *
 * **No shadow.** In dark, `ground` and `surface` are 2.5 lightness points
 * apart — too close for a fill to carry structure — and `rule` sits six points
 * clear of both. Structure here is drawn by hairlines; a shadow would claim the
 * list is floating above the page when it is sitting in it.
 */
export function RowList({
  className,
  role = "list",
  ...props
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      role={role}
      className={cn(
        // `overflow-clip`, never `overflow-hidden`. Both clip the rows to the
        // card's corners and look identical; only one of them is a scroll
        // container. `hidden` is, so it becomes the nearest scrollport for
        // everything inside it — and `RowGroup`'s sticky heading then measured
        // its offset from the top of this card instead of the top of the page.
        // The first heading sat 52px down, over its own first row, and no
        // heading ever stuck as the page scrolled, which is the whole of §6.1.
        // Measured: after scrolling past the card, `hidden` put the heading at
        // -147 and `clip` put it at 52, where it belongs.
        //
        // DECISION 14 moved this heading from `top-0` to the chrome's height
        // and turned an invisible heading into a visible overlap; the offset
        // was right and the scrollport underneath it was wrong the whole time.
        "divide-y divide-rule overflow-clip rounded-[var(--radius-card)] border border-rule bg-surface",
        className,
      )}
      {...props}
    />
  );
}

const row = cva("flex items-stretch", {
  variants: {
    /**
     * The 3px stripe at the row's left edge, carrying outcome.
     *
     * It is a stripe *and* a word in the row, never the stripe alone — severity
     * is never colour alone, because a reader who cannot separate the two hues
     * is then reading an unlabelled list. `neutral` is the `rule-strong` stripe
     * for a row with nothing notable about it; no `tone` at all draws no stripe,
     * which is right for a list whose rows have no outcome to report.
     */
    tone: {
      none: "",
      neutral: "border-l-3 border-l-rule-strong",
      positive: "border-l-3 border-l-positive",
      warning: "border-l-3 border-l-warning",
      critical: "border-l-3 border-l-critical",
      accent: "border-l-3 border-l-accent",
    },
  },
  defaultVariants: { tone: "none" },
});

/** A figure in a row: the number, and the word that says what it counts. */
export interface RowFigure {
  /** The figure itself. Set in mono, right-aligned, so a column of them lines up. */
  value: ReactNode;
  /** What it counts — `players`, `environments`. 11px, `ink-3`, under the figure. */
  label: string;
}

export interface RowProps
  extends Omit<HTMLAttributes<HTMLDivElement>, "children">, VariantProps<typeof row> {
  /** What happened. The row leads with this. */
  lead?: ReactNode;
  /**
   * The sentence that earns the row its place.
   *
   * *Is it live* and *is anything wrong* are two different facts, and the games
   * shelf used to put both in the same grey badge. This is where the second one
   * goes, in `ink-2`, under the first.
   */
  support?: ReactNode;
  /** A mark, avatar or monogram at the row's leading edge. */
  media?: ReactNode;
  /** Figures, right-aligned in mono, each labelled. */
  figures?: readonly RowFigure[];
  /** The identifier, small and dim. Copyable, never the thing the row leads with. */
  identifier?: ReactNode;
  /**
   * Anything the structured slots cannot say.
   *
   * Given without `lead`, it fills the row — which is how the lists that were
   * hand-built before this component existed keep working unchanged.
   */
  children?: ReactNode;
}

/**
 * One row.
 *
 * 44px minimum, always. The temptation at three hundred rows is to compress,
 * and `ui` at 13.5px in a 44px row is already the floor for a support person
 * reading under pressure. Density comes from grouping, collapsing and the
 * horizon — never from smaller type or tighter rows.
 *
 * What does not go in here: an action that is easy to hit while scanning, and a
 * second navigation. A row links to its own object and nowhere else.
 */
export function Row({
  className,
  tone,
  lead,
  support,
  media,
  figures,
  identifier,
  children,
  role = "listitem",
  ...props
}: RowProps) {
  return (
    <div
      role={role}
      className={cn(
        // Anything that scrolls a row into view — a keyboard focus, an anchor,
        // `scrollIntoView` — parks it flush at the top of the scroll area,
        // which is underneath the chrome and, in a grouped list, the 30px
        // heading sticking below it. The row arrives somewhere the reader
        // cannot see and cannot click. `scroll-mt` is the whole fix: leave room
        // for both, and a row scrolled to is a row you can reach.
        //
        // `--chrome-h` is how far down the chrome reaches, which is not always
        // the header's own height — see `RowGroup` below.
        "scroll-mt-[calc(var(--chrome-h,var(--header-h))+1.875rem)]",
        row({ tone }),
        className,
      )}
      {...props}
    >
      <div className="flex min-h-11 min-w-0 grow flex-wrap items-center gap-4 px-4 py-3 sm:flex-nowrap">
        {media ? <div className="shrink-0">{media}</div> : null}
        {lead === undefined && children !== undefined ? (
          <div className="min-w-0 grow">
            {children}
            {figures && figures.length > 0 && identifier !== undefined ? (
              <div className="font-mono text-micro break-all text-ink-3">{identifier}</div>
            ) : null}
          </div>
        ) : (
          <div className="flex w-full min-w-0 grow flex-col gap-1 break-words sm:w-auto">
            {lead !== undefined ? (
              <div className="text-ui font-semibold text-ink">{lead}</div>
            ) : null}
            {support !== undefined ? <div className="text-ui text-ink-2">{support}</div> : null}
            {children}
            {figures && figures.length > 0 && identifier !== undefined ? (
              <div className="font-mono text-micro break-all text-ink-3">{identifier}</div>
            ) : null}
          </div>
        )}
        {figures && figures.length > 0 ? (
          <div className="flex min-w-0 flex-wrap items-start gap-x-8 gap-y-2 text-left sm:ml-auto sm:shrink-0 sm:justify-end sm:text-right">
            {figures.map((figure) => (
              <div key={figure.label}>
                <div className="font-mono text-ui text-ink tabular-nums">{figure.value}</div>
                <div className="text-micro text-ink-3">{figure.label}</div>
              </div>
            ))}
          </div>
        ) : identifier !== undefined ? (
          <div className="w-full min-w-0 font-mono text-micro break-all text-ink-3 sm:w-auto sm:shrink-0">
            {identifier}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export interface RowGroupProps extends HTMLAttributes<HTMLDivElement> {
  /** The group's key — `7:32 PM`, `Live now`, `Published`. */
  label: ReactNode;
  /** How many rows are in it — `18 events`. A heading without a count is a label. */
  count?: ReactNode;
  /**
   * Classes for the sticky heading itself, not the group.
   *
   * The one real use is putting the offset back to `top-0` for a list that
   * scrolls inside its own container rather than with the page.
   */
  headingClassName?: string;
}

/**
 * Rows under a sticky heading carrying the group's key and its count.
 *
 * It sticks because a reader three hundred rows down a scroll region otherwise
 * has no way to tell which group they are in, and the answer — which minute,
 * which state — is usually the thing they came to find out.
 *
 * The group carries its own `divide-y` so the heading and its rows are divided
 * by the same hairline as everything else; `RowList`'s divider then separates
 * one group from the next.
 *
 * It sticks below the chrome rather than to zero, because both applications
 * put a strip across the top of the page and the page is the scroll region. At
 * `top-0` the heading slides underneath that strip and the reader loses the one
 * thing it exists to tell them, which is exactly the failure it was added to
 * prevent. `z-10` keeps it under the strip's own `z-20`.
 *
 * The offset is `--chrome-h`, falling back to `--header-h`, because those are
 * two different facts and only one of them is a token. `--header-h` is the
 * strip's own height, 52px, and it is right everywhere the chrome is one strip.
 * The console's phone frame is the exception: inside a project it gives the
 * environment a shelf of its own under the strip, so the chrome is two rows
 * deep and a heading stuck 52px down sits *inside* it, hidden — which is the
 * original bug at a width nobody looked at. A frame that is taller than its
 * strip sets `--chrome-h` on the element wrapping the page, and the fallback
 * means every other frame — the platform's, and the console's own desktop —
 * needs to do nothing.
 *
 * A list inside its own scroll container — a dialog, a pane — wants `top-0`
 * back; pass `className="top-0"` and tailwind-merge will drop this one.
 */
export function RowGroup({
  className,
  headingClassName,
  label,
  count,
  children,
  ...props
}: RowGroupProps) {
  return (
    <div data-row-group="" className={cn("divide-y divide-rule", className)} {...props}>
      <div
        // Something for a screenshot to aim at. A `position: sticky` element is
        // invisible to a full-page capture — the viewport is expanded to the
        // document's height first, so nothing is ever scrolled past and the
        // heading is drawn where it sits in the flow. This console has now
        // shipped a heading pinned underneath its own chrome twice, both times
        // found by reading the stylesheet. `e2e/shots/capture.spec.ts` selects
        // on this to take one viewport-sized, mid-scroll picture of the moment
        // that cannot otherwise be photographed.
        data-sticky-heading=""
        className={cn(
          // `pointer-events-none`: this heading is a label and a count, with
          // nothing in it to click, and it is pinned over the rows a reader is
          // trying to reach. A sticky overlay that swallows clicks meant for
          // what is underneath it is a bug in every case where it holds no
          // control — and it held one for a while here, silently, on every
          // grouped list. A caller that ever puts a control in a heading has to
          // re-enable events on that control.
          "sticky top-[var(--chrome-h,var(--header-h))] z-10 flex h-7.5 items-center justify-between gap-3",
          "pointer-events-none",
          "bg-sunk px-4 font-mono text-micro text-ink-3",
          headingClassName,
        )}
      >
        <span className="truncate">{label}</span>
        {count !== undefined ? <span className="shrink-0">{count}</span> : null}
      </div>
      {children}
    </div>
  );
}

export interface NewSinceProps extends Omit<HTMLAttributes<HTMLDivElement>, "onSelect"> {
  /** How many rows arrived since the reader last looked. */
  count: number;
  /** What arrived, if `rows` is the wrong word — `events`, `requests`. */
  noun?: string;
  /** Takes the reader to the newest row. A marker with no way back is a decoration. */
  onJump?: () => void;
  /** What the control says. */
  jumpLabel?: string;
}

/**
 * The one divider marking what arrived since the reader last looked.
 *
 * Drawn once, where the last view ended. A screen that repaints itself every
 * four seconds and gives no way to tell what is new is asking the reader to
 * hold three hundred rows in their head, which is the single biggest gap Live
 * events shipped with.
 */
export function NewSince({
  className,
  count,
  noun = "arrived",
  onJump,
  jumpLabel = "Jump to newest",
  ...props
}: NewSinceProps) {
  return (
    <div
      className={cn("flex items-center gap-3 bg-accent-soft px-4 py-2", className)}
      {...props}
    >
      <ArrowUpMark />
      <span className="text-meta font-medium text-accent-text">
        {count} {noun} since you opened this
      </span>
      {onJump ? (
        <Button size="sm" variant="ghost" className="ml-auto text-accent-text" onClick={onJump}>
          {jumpLabel}
        </Button>
      ) : null}
    </div>
  );
}

export interface RowHorizonProps extends HTMLAttributes<HTMLDivElement> {
  /** How far back the list goes, and how much of it there is. */
  summary: ReactNode;
  /** A second fact about the horizon — how long rows are kept, say. */
  note?: ReactNode;
  /** Loads the next window. Never an infinite scroll with no horizon. */
  onLoadEarlier?: () => void;
  /** What that control says. */
  loadLabel?: string;
}

/**
 * The footer that says how far back the reader is looking.
 *
 * `showing back to 7:28 PM · 312 in the last hour`. Without it a reader cannot
 * tell whether they have seen an hour or a minute, and a list that scrolls
 * forever with no horizon answers the question by never answering it.
 */
export function RowHorizon({
  className,
  summary,
  note,
  onLoadEarlier,
  loadLabel = "Load earlier",
  ...props
}: RowHorizonProps) {
  return (
    <div
      className={cn(
        "flex flex-wrap items-center gap-x-3 gap-y-1 bg-sunk px-4 py-3 font-mono text-micro",
        className,
      )}
      {...props}
    >
      <span className="text-ink-2">{summary}</span>
      {note !== undefined ? <span className="text-ink-3">{note}</span> : null}
      {onLoadEarlier ? (
        <Button size="sm" variant="secondary" className="ml-auto" onClick={onLoadEarlier}>
          {loadLabel}
        </Button>
      ) : null}
    </div>
  );
}

/** 16px stroke mark on a 24px grid, at 1.8, in `currentColor` — see the house rules. */
function ArrowUpMark() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className="size-4 shrink-0 text-accent-text"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 19V5M6 11l6-6 6 6" />
    </svg>
  );
}
